import * as React from 'react';
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import {
    IFalconConvenienceApiService,
    FalconNavigatorHeader,
    ICustomerPortalService,
    Policy,
    Types,
    CLAIM_TYPE_OTHER,
    POLICY_STATUS_MTC,
    Renewal,
    PolicyPropertyEnum,
    PolicyProperty,
    ProductsEnum,
    resourcesHelper,
} from "@falcon/services";
import {
    allowToRenderFutureMTAExplainedHeader,
    currentActivePolicyVersion,
    setSessionStorage,
} from "../../../../../CommonFunctions";
import {lazyInject} from "@athena-portal/core";
import {IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import FalconFooter from "../../../../../components/navigation/FalconFooter";
import FalconHeader from "../../../../../components/navigation/FalconHeader";
import {ButtonLink} from "../../../../../components/navigation/ButtonLink";
import * as moment from "moment";
import {HomePolicySummary} from "./HomePolicySummary";
import {AllianzOnlineBadge} from "../../../../../components/AllianzOnlineBadge";
import {FutureMTAExplainedHeader} from "../../motor/yourCarsPage/FutureMTAExplainedHeader";

interface IPageParams {
    policyNumber: string;
    [key: string]: string | undefined
}

interface ISettings {
    allianzDirectClaimsEnabled?: string | boolean;
}

const isSettingEnabled = (value: string | boolean | undefined): boolean => value === true || value === "true";

const buildDigitalClaimsUrl = (baseUrl: string | undefined, policyNumber: string): string | undefined => {
    if (!baseUrl) {
        return undefined;
    }

    const separator = baseUrl.includes("?") ? "&" : "?";

    return `${baseUrl}${separator}journey=digital&policy=${encodeURIComponent(policyNumber)}`;
};

export interface IState {
    futureActivePolicy?: Policy;
    currentlyActivePolicy?: Policy;
    renewalProposal?: Renewal;
    renewalEnabled: boolean;
    allianzDirectClaimsEnabled: boolean,
}

export class HomePolicySummaryPagePlain extends React.Component<IWithRouterProps<IPageParams>, IState> {
    protected pluginTemplates = new PluginTemplates();

    @lazyInject(Types.CustomerPortalService)
    protected customerPortalService: ICustomerPortalService;
    @lazyInject(Types.FalconConvenienceApiService)
    private readonly falconConvenienceApiService: IFalconConvenienceApiService;

    constructor(props: IWithRouterProps<IPageParams>) {
        super(props);

        this.state = {
            renewalEnabled: false,
            allianzDirectClaimsEnabled: false,
        }

    }

    public componentDidMount(): void {

        this.pluginTemplates.setPageTitle("Policy summary");
        this.loadAllianzDirectClaimsSwitch();

        this.customerPortalService.getPolicies(ProductsEnum.HOME).subscribe(
            (policies: Policy[] = []) => {
                const policy: Policy | undefined = policies
                    .filter(policy => policy.policyNumber === this.props.params.policyNumber)
                    .sort((a, b) => moment(b.endDate).diff(a.endDate))
                    .find(policy => policy !== undefined);

                this.setState({futureActivePolicy: policy})

                if (policy !== undefined) {
                    this.customerPortalService.getRenewalProposal(this.props.params.policyNumber).subscribe((renewalProposal: Renewal) => {
                        this.setState({renewalProposal})
                    });

                    this.customerPortalService.getPolicyPropertyByPropertyKey(this.props.params.policyNumber, PolicyPropertyEnum.PERFORM_RENEWAL).subscribe(
                        (property: PolicyProperty) => {
                            this.setState({
                                renewalEnabled: property.value === "true"
                            });
                        }
                    );
                }
                });

        this.customerPortalService.getPolicyHistory().subscribe(
            (allPolicies: Policy[] = []) => {
                this.setState({
                    currentlyActivePolicy: currentActivePolicyVersion(
                        allPolicies,
                        this.props.params.policyNumber,
                        ProductsEnum.HOME
                    )
                });
            }
        );
    }

    private prepareClaimsTileLink(): { claimsLink: string; useHomeDirectClaimsLink: boolean } {
        const defaultClaimsLink =`/dashboard/annualHome/${this.props.params.policyNumber}/claims/new`

        const homeDirectClaimsLink = buildDigitalClaimsUrl(
            process.env.REACT_APP_HOME_OTHER_CLAIM_URL,
            this.props.params.policyNumber
        );

        const useHomeDirectClaimsLink = resourcesHelper.THEME !== "Britannia" && this.state.allianzDirectClaimsEnabled && homeDirectClaimsLink != null;

        return {
            claimsLink: useHomeDirectClaimsLink && homeDirectClaimsLink
                ? homeDirectClaimsLink
                : defaultClaimsLink,
            useHomeDirectClaimsLink
        };
    }

    public render(): React.ReactNode {
        const {futureActivePolicy, currentlyActivePolicy} = this.state;
        const dataLoaded = Boolean(futureActivePolicy && currentlyActivePolicy);

        if (dataLoaded) {
            const policy: Policy = futureActivePolicy!;
            const interaction = policy.interaction;
            const backLinkUrl: string = `/dashboard/annualHome/${this.props.params.policyNumber}`;
            const {claimsLink, useHomeDirectClaimsLink} = this.prepareClaimsTileLink();

            if (!useHomeDirectClaimsLink && interaction.annualHomeFixedAddons?.homeEmergency) {
                setSessionStorage("claim", CLAIM_TYPE_OTHER);
            }

            const policyStarted = policy && policy.status !== POLICY_STATUS_MTC && moment().isAfter(moment(policy.startDate));

            return (
                <>
                    <FalconHeader/>
                    <div className="page-contents home-summary-page">
                        <AllianzOnlineBadge/>
                        <FalconNavigatorHeader back={backLinkUrl} title="Policy summary"/>
                        {this.renderPolicyNumber()}
                        {
                            allowToRenderFutureMTAExplainedHeader(interaction.process) &&
                            <FutureMTAExplainedHeader
                                policyNumber={this.props.params.policyNumber}
                                policyEndDate={policy.endDate}
                                upcomingMtaDate={policy.annualPolicyInfo.maxFutureMtaDate}
                            />
                        }
                        {this.renderSummary()}
                        {this.renderCancelPolicyButton(policy?.status === POLICY_STATUS_MTC, policy)}
                        {policyStarted && <ButtonLink
                            primaryNoArrow={true}
                            className="make-a-claim"
                            to={claimsLink}>
                            Make a claim!
                        </ButtonLink>}
                        {this.pluginTemplates.renderBackLink("Back", false, backLinkUrl)}
                    </div>
                    <FalconFooter/>
                </>
            )
        } else {
            return <Overlay show={true}/>
        }
    }


    private renderSummary() {
            this.setActivePolicyEndDate();

            return (
                <React.Fragment key={this.state.futureActivePolicy!.policyNumber}>
                    <div className="section-panel your-cover-panel padding-top-bot-50">
                        <HomePolicySummary
                            policy={this.state.futureActivePolicy!}
                            currentlyActivePolicy={this.state.currentlyActivePolicy}
                            editAddress={true}
                            isOldPolicy={false}
                            hasDebt={this.hasDebt()}
                        />
                    </div>
                </React.Fragment>
            );
    }

    private hasDebt() {
        const {hasDebt, hasUnpaidChargebackInvoice, hasUnpaidChange} = this.state.currentlyActivePolicy?.annualPolicyInfo || this.state.futureActivePolicy!.annualPolicyInfo;
        return hasDebt || hasUnpaidChargebackInvoice || hasUnpaidChange;
    }

    private setActivePolicyEndDate(): void {
        this.state.currentlyActivePolicy!.endDate = this.state.futureActivePolicy!.endDate;
    }

    private renderPolicyNumber(): React.ReactNode {
        return (
            <div className="page-heading-white">
                <span className="font-weight-light">Policy no: </span>
                {this.props.params.policyNumber}
            </div>
        );
    }

    private renderCancelPolicyButton(isDisabled: boolean, policy: Policy): React.ReactNode {
        if (this.state.renewalProposal && this.state.renewalEnabled) {
            return (
                <ButtonLink
                    to={`/dashboard/home/cancel/${this.props.params.policyNumber}`}
                    routeState={{
                        mtcJourneyUrl: `/journey/init/product/AnnualHomeInsurance/process/mtc/presetPage/${policy.interaction?._id}`,
                        renewalCancellationUrl: `/journey/show/product/AnnualHomeInsurance/process/renewal/cancellationPresetPage/${this.state.renewalProposal._id}`,
                        pageTitle: "Cancel home policy",
                        questionTitle: "When would you like your home policy to end?"
                    }}
                    disabled={isDisabled}
                >
                    Cancel policy
                </ButtonLink>
            )
        } else {
            return (
                <ButtonLink to={`/journey/init/product/AnnualHomeInsurance/process/mtc/presetPage/${policy.interaction._id}`} disabled={isDisabled}>
                    Cancel policy
                </ButtonLink>
            )
        }
    }

    private loadAllianzDirectClaimsSwitch(): void {
        this.falconConvenienceApiService
            .getSettings()
            .subscribe((settings: ISettings): void => {
                this.setState({
                    allianzDirectClaimsEnabled: isSettingEnabled(settings?.allianzDirectClaimsEnabled)
                });
            });
    }
}

export const HomePolicySummaryPage = withRouter<{}, IPageParams>(HomePolicySummaryPagePlain)
